import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, Flex, Portal, Text, useColorModeValue } from '@chakra-ui/react';
import { resolveDefaultModelId } from 'helpers/lib';
import { Sparkles } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Model } from 'services/model/model.type';

interface ModelSelectorProps {
  selectedModelId?: string;
  onModelChange: (model: Model | null) => void;
  isDisabled?: boolean;
  width?: string | number;
  maxWidth?: string | number;
  placeholder?: string;
  /** 'top' opens upward (composer at the bottom of the screen); 'bottom' opens downward. */
  menuPlacement?: 'auto' | 'top' | 'bottom';
  /** Render the pill with no border/background (e.g. inside a wrapper that supplies the surface). */
  transparentControl?: boolean;
}

const MENU_W = 280;

/**
 * Plain custom model dropdown (mockup ModelSelector) — replaces chakra-react-select, which kept
 * fighting us on z-index / width / transparent menu bg. Full control: a Portal'd, fully-opaque
 * menu at a high z-index that reliably sits above the composer. Pill = Sparkles + name + chevron;
 * options = green dot + name + description + check. Only support:true models are selectable.
 */
const ModelSelector = ({
  selectedModelId,
  onModelChange,
  isDisabled = false,
  maxWidth = '220px',
  placeholder,
  menuPlacement = 'auto',
  transparentControl = false,
}: ModelSelectorProps) => {
  const models = useSelector((state: any) => state.model?.models || []);
  const isLoading = useSelector((state: any) => state.model?.isLoading || false);

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top?: number; bottom?: number } | null>(
    null
  );
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const surface = useColorModeValue('#ffffff', '#1f2430'); // opaque menu/pill bg
  const textColor = useColorModeValue('#16181d', '#ffffff');
  const subtle = useColorModeValue('#6b7280', '#9ba1ac');
  const border = useColorModeValue('#d0d4da', '#3a3f4b');
  const hoverBg = useColorModeValue('#f3f3fb', '#2a2f3a');

  // Only support:true models are selectable (undefined → supported, back-compat).
  const selectable = useMemo(() => models.filter((m: Model) => m.support !== false), [models]);
  // Selected from the FULL list so a chat using a now-unsupported model still shows its name.
  const selected = models.find((m: Model) => m.id === selectedModelId) || null;

  // Default to the latest supported model.
  useEffect(() => {
    if (!selectedModelId && selectable.length > 0 && !isLoading) {
      const defId = resolveDefaultModelId(models);
      const def = selectable.find((m: Model) => m.id === defId) || selectable[0];
      onModelChange(def);
    }
  }, [selectedModelId, selectable, isLoading, onModelChange, models]);

  const computeCoords = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    if (menuPlacement === 'bottom') {
      setCoords({ left: r.left, top: r.bottom + 8 });
    } else {
      // open upward by anchoring the menu's bottom just above the pill
      setCoords({ left: r.left, bottom: window.innerHeight - r.top + 8 });
    }
  }, [menuPlacement]);

  const toggle = () => {
    if (isDisabled) return;
    if (!open) computeCoords();
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node))
        return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    // Keep the fixed menu aligned to the pill while scrolling/resizing — DON'T close on scroll
    // (a capture-phase scroll listener was closing it instantly on any inner scroll).
    const reposition = () => computeCoords();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, computeCoords]);

  const label =
    selected?.display_name ||
    selected?.name ||
    (isLoading ? 'Loading models...' : placeholder || 'Select model');

  return (
    // Pill fits its content (mockup model-pill is inline-flex), not stretched to maxWidth.
    <Box display='inline-flex' w='fit-content' maxW={maxWidth}>
      <Flex
        ref={btnRef}
        as='button'
        align='center'
        gap={2}
        h='38px'
        px={3}
        bg={transparentControl ? 'transparent' : surface}
        opacity={isDisabled ? 0.6 : 1}
        borderWidth={transparentControl ? '0' : '1px'}
        borderStyle='solid'
        borderColor={transparentControl ? 'transparent' : border}
        borderRadius='full'
        _hover={transparentControl ? {} : { borderColor: 'primary.solid' }}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        transition='border-color .16s ease-out'
        aria-expanded={open}
        aria-haspopup='listbox'
        aria-label={placeholder || 'Select model'}
        onClick={toggle}
        role='combobox'
        type='button'
      >
        <Box as={Sparkles} flexShrink={0} boxSize='15px' color='primary.solid' />
        <Text
          flex={1}
          color={textColor}
          fontSize='13px'
          fontWeight={600}
          textAlign='left'
          noOfLines={1}
        >
          {label}
        </Text>
        <Box
          as={open ? ChevronUpIcon : ChevronDownIcon}
          flexShrink={0}
          boxSize='16px'
          color={subtle}
        />
      </Flex>

      {open && coords && (
        <Portal>
          <Box
            ref={menuRef}
            sx={{
              animation: 'msFade .14s ease-out',
              '@keyframes msFade': {
                from: { opacity: 0, transform: 'translateY(4px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
            pos='fixed'
            zIndex={99999}
            top={coords.top !== undefined ? `${coords.top}px` : undefined}
            bottom={coords.bottom !== undefined ? `${coords.bottom}px` : undefined}
            left={`${coords.left}px`}
            w={`${MENU_W}px`}
            p={2}
            bg={surface}
            borderWidth='1px'
            borderColor={border}
            borderRadius='lg'
            shadow='0 12px 32px -8px rgba(20,24,34,.24)'
          >
            <Text
              px={3}
              pt={1}
              pb={1}
              color={subtle}
              fontSize='11px'
              fontWeight={700}
              letterSpacing='0.06em'
              textTransform='uppercase'
            >
              Model
            </Text>
            {selectable.map((m: Model) => {
              const isSel = m.id === selectedModelId;
              return (
                <Flex
                  key={m.id}
                  as='button'
                  align='stretch'
                  direction='column'
                  gap='2px'
                  w='100%'
                  px={3}
                  py={2}
                  color={isSel ? '#ffffff' : textColor}
                  textAlign='left'
                  bg={isSel ? 'primary.solid' : 'transparent'}
                  borderRadius='md'
                  _hover={{ bg: isSel ? 'primary.solid' : hoverBg }}
                  aria-selected={isSel}
                  onClick={() => {
                    onModelChange(m);
                    setOpen(false);
                  }}
                  role='option'
                  type='button'
                >
                  <Flex align='center' gap={2}>
                    <Box
                      flexShrink={0}
                      boxSize='8px'
                      bg={isSel ? '#ffffff' : 'state.success'}
                      borderRadius='full'
                    />
                    <Text flex={1} fontSize='14px' fontWeight={500} noOfLines={1}>
                      {m.display_name || m.name}
                    </Text>
                    {isSel && <Box as={CheckIcon} flexShrink={0} boxSize='14px' color='#ffffff' />}
                  </Flex>
                  {m.description && (
                    <Text
                      color={isSel ? '#ffffff' : subtle}
                      fontSize='12px'
                      opacity={isSel ? 0.9 : 0.7}
                      noOfLines={1}
                    >
                      {m.description}
                    </Text>
                  )}
                </Flex>
              );
            })}
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default memo(ModelSelector);
